import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PAYMENT_METHODS = ["Cash", "Credit Card", "Debit Card", "UPI", "Bank Transfer", "Wallet", "Other"];
const ADD_NEW = "__add_new__";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function TransactionDialog({ open, onOpenChange, kind, editing, categories, onSaved }) {
  const isExpense = kind === "expense";

  const [form, setForm] = useState(() => emptyState(isExpense));
  const [saving, setSaving] = useState(false);

  // Local override for newly-created categories (income or expense) so they show up immediately.
  // Shape: [{ id, name, type }]
  const [extraCategories, setExtraCategories] = useState([]);

  // Add-new popup state
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState(null); // "source" (income) | "category" (expense)
  const [addName, setAddName] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  // Controlled open flag for the Select so we can close it before opening the popup
  const [selectOpen, setSelectOpen] = useState(false);

  function emptyState(expense) {
    return expense
      ? { amount: "", category: "", date: todayISO(), payment_method: "Cash", notes: "" }
      : { amount: "", source: "", date: todayISO(), notes: "" };
  }

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        amount: editing.amount ?? "",
        date: (editing.date || todayISO()).slice(0, 10),
        notes: editing.notes ?? "",
        ...(isExpense
          ? { category: editing.category || "", payment_method: editing.payment_method || "Cash" }
          : { source: editing.source || "" }),
      });
    } else {
      setForm(emptyState(isExpense));
    }
    setExtraCategories([]);
  }, [open, editing, isExpense]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openAddNew = (which) => {
    setSelectOpen(false);
    setAddKind(which);
    setAddName("");
    setTimeout(() => setAddOpen(true), 80);
  };

  const saveAddNew = async () => {
    const name = addName.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    setAddBusy(true);
    try {
      const type = addKind === "source" ? "income" : "expense";
      const { data } = await api.post("/categories", { name, type });
      setExtraCategories((prev) =>
        prev.some((c) => c.id === data.id) ? prev : [...prev, data]
      );
      const createdName = data.name;
      const wasSource = addKind === "source";
      setAddOpen(false);
      setAddName("");
      // Defer selection until after the popup fully unmounts + Radix Select settles
      setTimeout(() => {
        if (wasSource) update("source", createdName);
        else update("category", createdName);
      }, 150);
      toast.success(wasSource ? "Source added" : "Category added");
    } catch (err) {
      toast.error(formatApiError(err, "Could not save"));
    } finally {
      setAddBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        amount: Number(form.amount),
        date: form.date,
        notes: form.notes || "",
        ...(isExpense
          ? { category: form.category, payment_method: form.payment_method }
          : { source: form.source }),
      };
      if (!payload.amount || payload.amount <= 0) {
        toast.error("Amount must be greater than 0");
        return;
      }
      if (isExpense && !payload.category) {
        toast.error("Please choose a category");
        return;
      }
      if (!isExpense && !payload.source) {
        toast.error("Please choose a source");
        return;
      }
      const url = isExpense ? "/expenses" : "/incomes";
      if (editing) {
        await api.patch(`${url}/${editing.id}`, payload);
        toast.success(isExpense ? "Expense updated" : "Income updated");
      } else {
        await api.post(url, payload);
        toast.success(isExpense ? "Expense added" : "Income added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Merge props + locally-added categories, filter by type, dedupe by name, sort.
  const filteredCategories = (type) => {
    const base = (categories || []).filter((c) => c.type === type);
    const merged = [...base];
    for (const c of extraCategories) {
      if (c.type === type && !merged.some((m) => m.name === c.name)) merged.push(c);
    }
    return merged.sort((a, b) => a.name.localeCompare(b.name));
  };

  const expenseCategories = filteredCategories("expense");
  const incomeSources = filteredCategories("income");

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid={`${kind}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Edit" : "Add"} {isExpense ? "Expense" : "Income"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => update("amount", e.target.value)}
                data-testid={`${kind}-amount-input`}
                placeholder="0.00"
              />
            </div>

            {isExpense ? (
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select
                  value={form.category}
                  open={selectOpen}
                  onOpenChange={setSelectOpen}
                  onValueChange={(v) => {
                    if (v === ADD_NEW) {
                      openAddNew("category");
                      return;
                    }
                    update("category", v);
                  }}
                >
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                    <div className="my-1 h-px bg-border" />
                    <SelectItem
                      value={ADD_NEW}
                      className="text-primary font-medium focus:text-primary"
                      data-testid="expense-category-add-new"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <Plus className="h-3.5 w-3.5" /> Add New Category
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Select
                  value={form.source}
                  open={selectOpen}
                  onOpenChange={setSelectOpen}
                  onValueChange={(v) => {
                    if (v === ADD_NEW) {
                      openAddNew("source");
                      return;
                    }
                    update("source", v);
                  }}
                >
                  <SelectTrigger data-testid="income-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {incomeSources.map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                    <div className="my-1 h-px bg-border" />
                    <SelectItem
                      value={ADD_NEW}
                      className="text-primary font-medium focus:text-primary"
                      data-testid="income-source-add-new"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <Plus className="h-3.5 w-3.5" /> Add New Source
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isExpense && (
              <div className="space-y-1.5">
                <Label>Payment method</Label>
                <Select value={form.payment_method} onValueChange={(v) => update("payment_method", v)}>
                  <SelectTrigger data-testid="expense-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                required
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                data-testid={`${kind}-date-input`}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                rows={3}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Optional details"
                data-testid={`${kind}-notes-input`}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid={`${kind}-submit-button`}
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Small popup for adding a new Source / Category */}
      <Dialog open={addOpen} onOpenChange={(o) => !addBusy && setAddOpen(o)}>
        <DialogContent className="sm:max-w-sm" data-testid="add-new-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              {addKind === "source" ? "Add new source" : "Add new category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="add-new-name">Name</Label>
            <Input
              id="add-new-name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder={
                addKind === "source"
                  ? "e.g. Salary, Freelance, Rental"
                  : "e.g. Groceries, Fuel, Rent"
              }
              autoFocus
              maxLength={60}
              data-testid="add-new-name-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveAddNew();
                }
              }}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAddOpen(false)}
              disabled={addBusy}
              data-testid="add-new-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveAddNew}
              disabled={addBusy || !addName.trim()}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="add-new-save-button"
            >
              {addBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
